import { comuneLabel } from '@sse/gazetteer';
import type { Event } from '@sse/schema';
import { ArrowLeftRight, ExternalLink, FilePlus, Merge, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useNavigate } from 'react-router';
import remarkGfm from 'remark-gfm';
import { ApiError, type CandidateDetail, type CandidateRow, type DedupMatch } from '../lib/api';
import {
  FIELD_LABELS,
  REJECT_REASONS,
  categoryLabel,
  formatRange,
  formatStamp,
  whoLabel,
} from '../lib/format';
import { useSession } from '../lib/session';
import { CandidateStatusText, OutcomeBadge } from './imports';
import { StatusBadge, btnGhost, btnPrimary, btnSecondary } from './ui';

type Side = 'event' | 'candidate';
type Origin = Side | 'filled';
const FIELDS = ['title', 'dates', 'place', 'category', 'texts', 'links'] as const;
type Field = (typeof FIELDS)[number];
export const FIELD_LABEL: Record<Field, string> = {
  title: 'Titolo',
  dates: 'Date e orari',
  place: 'Luogo',
  category: 'Categoria',
  texts: 'Testi',
  links: 'Link',
};

interface MergePreview {
  event: Event;
  changes: Record<string, unknown>;
  origin: Record<Field, Origin>;
  available: Record<Field, { event: boolean; candidate: boolean }>;
  eventVersion: number;
}

type Dates = Array<{ date: string; start?: string | undefined; end?: string | undefined }>;
const dayFmt = new Intl.DateTimeFormat('it-IT', {
  timeZone: 'UTC',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});
const comuneName = (id?: string) => comuneLabel(id);
const score = (n: number) => n.toFixed(2).replace('.', ',');

/** Scorciatoie del pannello (mostrate anche nei suggerimenti dei pulsanti). */
export const REVIEW_KEYS =
  'j/k: successivo/precedente · u: aggiorna l’evento esistente · t: cambia versione tenuta · c: crea come nuovo · b: bozza · s: non è un evento';

export function DatesView({ dates }: { dates: Dates }) {
  if (dates.length === 0) return <>—</>;
  if (dates.length <= 4)
    return (
      <ul>
        {dates.map((d) => (
          <li key={d.date}>
            {dayFmt.format(new Date(`${d.date}T12:00:00Z`))}
            {/* anno solo se diverso da quello corrente (es. eventi del prossimo anno) */}
            {d.date.slice(0, 4) !== String(new Date().getFullYear()) && ` ${d.date.slice(0, 4)}`}
            {d.start && <span className="text-muted"> · {d.start}</span>}
          </li>
        ))}
      </ul>
    );
  const times = [...new Set(dates.map((d) => d.start).filter(Boolean))];
  return (
    <>
      {formatRange(dates[0]!.date, dates.at(-1)!.date)} · {dates.length} giorni
      {times.length > 0 && <div className="text-muted">ore {times.join(', ')}</div>}
    </>
  );
}

export const PlaceView = ({
  p,
}: {
  p: { comuneId?: string; frazione?: string; venue?: Event['venue']; text?: string };
}) => (
  <>
    {[p.frazione, comuneName(p.comuneId)].filter(Boolean).join(', ') || p.text || '—'}
    {p.venue?.name && <div className="text-muted">{p.venue.name}</div>}
  </>
);

const LINK_TYPE: Record<string, string> = {
  source: 'fonte',
  official: 'sito ufficiale',
  tickets: 'biglietti',
  social: 'social',
  other: 'link',
};

export const LinksView = ({ links }: { links: Array<{ url: string; type: string }> }) =>
  links.length ? (
    <ul>
      {links.map((l) => (
        <li key={l.url} className="truncate">
          {LINK_TYPE[l.type] ?? l.type} ·{' '}
          <a href={l.url} target="_blank" rel="noopener" className="hover:underline">
            {l.url.replace(/^https?:\/\//, '')}
          </a>
        </li>
      ))}
    </ul>
  ) : (
    <>—</>
  );

/**
 * Revisione di un candidato. Con un evento simile: le due versioni affiancate campo per campo;
 * si sceglie quale tenere e, cliccando una cella, si prende quel campo dall'altra versione
 * (sovrascrivendo). Dove la versione tenuta non ha un'informazione e l'altra sì, si completa da
 * sola. L'anteprima arriva dall'API, che applica le stesse regole all'unione.
 */
export function ReviewPanel({
  id,
  onDecided,
}: {
  id: string;
  onDecided: (row: CandidateRow, eventId?: string) => void;
}) {
  const { api, config } = useSession();
  const [data, setData] = useState<CandidateDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState(0);
  const [keep, setKeep] = useState<Side>('event');
  const [picks, setPicks] = useState<Partial<Record<Field, Side>>>({});
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [rewriting, setRewriting] = useState(false);
  const [rewriteFailed, setRewriteFailed] = useState(false);
  /** scelta del motivo dello scarto aperta */
  const [rejecting, setRejecting] = useState(false);
  const navigate = useNavigate();
  const [rejectNote, setRejectNote] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await api<CandidateDetail>(`/imports/candidates/${id}`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [api, id]);

  useEffect(() => {
    setData(null);
    setError(null);
    setTab(0);
    setKeep('event');
    setPicks({});
    setRewriteFailed(false);
    setRejecting(false);
    setRejectNote('');
    void load();
  }, [load]);

  const matches = data?.candidate.matches ?? [];
  const match: DedupMatch | undefined = matches[tab];
  const event = match?.kind === 'event' ? data?.events.find((e) => e.id === match.id) : undefined;
  const open = data && (data.row.status === 'needs_review' || data.row.status === 'pending');
  const canCreate = Boolean(data?.candidate.normalized?.comuneId);
  const enriched = Boolean(data?.candidate.enriched);
  const hasSourceText = Boolean(data?.candidate.extracted.description);
  // testi della sorgente scelti (cella o versione tenuta): si pubblicano solo riscritti
  const wantsSourceTexts =
    picks.texts === 'candidate' || (keep === 'candidate' && picks.texts !== 'event');
  const needsRewrite = Boolean(event && open && wantsSourceTexts && !enriched && hasSourceText);
  /** scelte inviate all'API: i testi della sorgente solo quando sono già riscritti */
  const effectivePicks = useMemo(() => {
    if (enriched) return picks;
    const { texts: _texts, ...rest } = picks;
    void _texts;
    return rest;
  }, [enriched, picks]);

  // anteprima dell'unione a ogni scelta
  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    if (!data || !event || !open) return;
    let alive = true;
    api<MergePreview>(`/imports/candidates/${data.row.id}/merge-preview`, {
      method: 'POST',
      body: { eventId: event.id, keep, picks: effectivePicks },
    })
      .then((p) => alive && setPreview(p))
      .catch((e) => alive && setPreviewError(e instanceof ApiError ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [api, data, event, open, keep, effectivePicks]);

  const decide = useCallback(
    async (body: Record<string, unknown>) => {
      if (!data || busy) return;
      setBusy(true);
      setError(null);
      try {
        const r = await api<{ candidate: CandidateRow; eventId?: string }>(
          `/imports/candidates/${data.row.id}/decision`,
          { method: 'POST', body: { version: data.row.version, ...body } },
        );
        onDecided(r.candidate, r.eventId);
        // bozza: si apre subito la scheda per modificarla prima di pubblicarla
        if (body.action === 'create' && body.status === 'draft' && r.eventId)
          navigate(`/events/${r.eventId}`);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [api, busy, data, onDecided, navigate],
  );

  const reject = (reason: string) =>
    void decide({ action: 'reject', reason, ...(rejectNote.trim() ? { note: rejectNote } : {}) });

  const merge = useCallback(() => {
    if (!event || !preview || rewriting) return;
    void decide({
      action: 'merge',
      eventId: event.id,
      keep,
      picks: effectivePicks,
      eventVersion: preview.eventVersion,
    });
  }, [decide, event, keep, effectivePicks, preview, rewriting]);

  const toggleKeep = () => {
    setRewriteFailed(false);
    setKeep((k) => (k === 'event' ? 'candidate' : 'event'));
    setPicks({});
  };
  /** clic su una cella: quel campo si prende da quella versione */
  const pickField = (f: Field, side: Side) => {
    if (f === 'texts') setRewriteFailed(false); // una nuova scelta ritenta la riscrittura
    setPicks((p) => ({ ...p, [f]: side }));
  };

  // scelti i testi della sorgente non ancora riscritti: si riscrivono subito con l'AI
  useEffect(() => {
    if (!needsRewrite || rewriting || rewriteFailed || !data) return;
    setRewriting(true);
    setError(null);
    api(`/imports/candidates/${data.row.id}/enrich`, { method: 'POST', body: {} })
      .then(() => load())
      .catch((e) => {
        setRewriteFailed(true);
        setError(
          `Testo non riscritto (${e instanceof ApiError ? e.message : String(e)}): resta il testo dell’evento`,
        );
      })
      .finally(() => setRewriting(false));
  }, [api, data, load, needsRewrite, rewriting, rewriteFailed]);

  // scorciatoie da tastiera (non mentre si scrive in un campo)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (!open || e.metaKey || e.ctrlKey || e.altKey) return;
      if (t && ['INPUT', 'SELECT', 'TEXTAREA'].includes(t.tagName)) return;
      if (e.key === 'u' && event) merge();
      else if (e.key === 't' && event) toggleKeep();
      else if (e.key === 'c' && canCreate) void decide({ action: 'create', status: 'published' });
      else if (e.key === 'b' && canCreate) void decide({ action: 'create', status: 'draft' });
      else if (e.key === 's') setRejecting((r) => !r);
      else if (e.key === 'Escape') setRejecting(false);
      else if (rejecting && /^[1-6]$/.test(e.key))
        reject(Object.keys(REJECT_REASONS)[Number(e.key) - 1]!);
      else if (/^[1-3]$/.test(e.key) && matches[Number(e.key) - 1]) {
        setTab(Number(e.key) - 1);
        setPicks({});
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canCreate, event, matches, decide, merge, rejecting, rejectNote]);

  if (error && !data)
    return (
      <p role="alert" className="bg-danger-soft p-3 text-sm">
        Errore: {error}
      </p>
    );
  if (!data) return <p className="p-4 text-sm text-muted">Caricamento…</p>;

  const { candidate: c, row } = data;
  const n = c.normalized;
  const en = c.enriched;
  const candDates: Dates = n?.dates ?? c.extracted.dates;

  // contenuto delle celle per le due versioni. Un campo dell'evento completato con la
  // sorgente si mostra già completato (valore dell'anteprima), così si vede il risultato.
  const shown = (f: Field): Event | undefined =>
    preview && preview.origin[f] === 'filled' ? preview.event : event;
  const ev = (f: Field) => shown(f);
  const cell: Record<Field, { candidate: ReactNode; event: ReactNode }> = {
    title: { candidate: row.title, event: event?.title },
    dates: {
      candidate: <DatesView dates={candDates} />,
      event: ev('dates') && <DatesView dates={ev('dates')!.dates} />,
    },
    place: {
      candidate: (
        <PlaceView
          p={{
            comuneId: n?.comuneId,
            frazione: n?.frazione,
            venue: n?.venue,
            text: c.extracted.placeText,
          }}
        />
      ),
      event: ev('place') && (
        <PlaceView
          p={{
            comuneId: ev('place')!.comuneId,
            frazione: ev('place')!.frazione,
            venue: ev('place')!.venue,
          }}
        />
      ),
    },
    category: {
      candidate: categoryLabel(row.category),
      event: event && categoryLabel(event.category),
    },
    texts: {
      candidate: en ? (
        <>
          <p className="font-medium">{en.shortDesc}</p>
          <p className="mt-1 whitespace-pre-line">{en.description}</p>
        </>
      ) : (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted">
            {row.outcome === 'new' ? 'Testi riscritti in arrivo. ' : ''}Testo originale (non si
            pubblica):
          </span>
          <p className="whitespace-pre-line text-muted">{c.extracted.description || '—'}</p>
          {rewriting && (
            <span className="text-sm font-medium text-fg">
              Riscrivo il testo con l’AI per poterlo pubblicare…
            </span>
          )}
        </div>
      ),
      event: ev('texts') && (
        <>
          {ev('texts')!.shortDesc && <p className="font-medium">{ev('texts')!.shortDesc}</p>}
          <div className="mt-1 flex flex-col gap-2">
            {ev('texts')!.description ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{ev('texts')!.description}</ReactMarkdown>
            ) : (
              '—'
            )}
          </div>
        </>
      ),
    },
    links: {
      // la pagina della sorgente entra nell'evento come link di riferimento ("Fonte")
      candidate: (
        <LinksView
          links={[
            ...(n?.links ?? []).filter((l) => l.type !== 'source'),
            ...(row.sourceUrl ? [{ type: 'source', url: row.sourceUrl }] : []),
          ]}
        />
      ),
      event: ev('links') && <LinksView links={ev('links')!.links ?? []} />,
    },
  };

  const changes = preview
    ? [
        ...new Set(
          Object.keys(preview.changes)
            .filter((k) => k !== 'startDate' && k !== 'endDate' && k !== 'geo')
            .map((k) => (FIELD_LABELS[k] ?? k).toLowerCase()),
        ),
      ]
    : [];
  const CreateButtons = () => (
    <>
      <button
        disabled={busy || !canCreate}
        onClick={() => void decide({ action: 'create', status: 'published' })}
        className={event ? btnSecondary : btnPrimary}
        title={
          canCreate
            ? 'Crea un nuovo evento con la versione della sorgente e pubblicalo (c)'
            : 'Comune non riconosciuto: apri la scheda completa'
        }
      >
        <Plus className="size-4" aria-hidden />{' '}
        {event ? 'Crea come nuovo evento' : 'Crea e pubblica'}
      </button>
      <button
        disabled={busy || !canCreate}
        onClick={() => void decide({ action: 'create', status: 'draft' })}
        className={btnGhost}
        title="Crea il nuovo evento come bozza e apre la scheda per modificarlo prima di pubblicarlo (b)"
      >
        <FilePlus className="size-4" aria-hidden /> Crea e modifica
      </button>
      <span className="ml-auto" />
      <button
        disabled={busy}
        onClick={() => setRejecting((r) => !r)}
        className={btnGhost}
        aria-expanded={rejecting}
        title="Non va pubblicato: esce dalla coda senza creare nulla, con il motivo (s)"
      >
        <X className="size-4" aria-hidden /> Non è un evento da pubblicare
      </button>
      {rejecting && (
        <div className="flex basis-full flex-wrap items-center gap-2 bg-surface p-2">
          <span className="text-label font-medium uppercase tracking-[0.04em] text-muted">
            Perché?
          </span>
          {Object.entries(REJECT_REASONS).map(([k, label], i) => (
            <button
              key={k}
              disabled={busy}
              onClick={() => reject(k)}
              className={btnSecondary}
              title={`Scarta: ${label.toLowerCase()} (${i + 1})`}
            >
              {label}
            </button>
          ))}
          <input
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            maxLength={300}
            placeholder="Nota (facoltativa)"
            aria-label="Nota sullo scarto"
            className="min-h-9 min-w-0 flex-1 border border-surface-strong bg-page px-2 text-sm"
          />
        </div>
      )}
    </>
  );
  return (
    <div className="flex flex-col gap-3">
      {/* intestazione */}
      <div className="flex flex-wrap items-center gap-2">
        <OutcomeBadge outcome={row.outcome} />
        <CandidateStatusText row={row} />
        <span className="text-xs text-muted">
          {c.sourceId} · confidenza {score(c.confidence)}
        </span>
        {row.sourceUrl && (
          <a
            href={row.sourceUrl}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1 text-sm font-medium underline-offset-4 hover:underline"
            title={row.sourceUrl}
          >
            Pagina della sorgente <ExternalLink className="size-3.5" aria-hidden />
          </a>
        )}
        <Link to={`/review/${row.id}`} className="ml-auto text-xs text-muted hover:underline">
          scheda completa
        </Link>
      </div>

      {/* locandina della sorgente (per le segnalazioni è nel riquadro sotto) */}
      {data.poster && !data.submission && (
        <div className="flex items-start gap-3 bg-surface p-3 text-sm">
          <a href={data.poster.url} target="_blank" rel="noopener">
            <img
              src={data.poster.url}
              alt="Locandina della sorgente"
              className="h-28 w-auto border border-surface-strong object-contain"
            />
          </a>
          <span className="text-muted">
            Locandina della sorgente: all’approvazione diventa l’immagine dell’evento, se non ne ha
            già una.
          </span>
        </div>
      )}

      {/* segnalazione dal sito: chi l'ha mandata e la locandina originale */}
      {data.submission && (
        <div className="flex flex-wrap items-start gap-3 bg-surface p-3 text-sm">
          {data.submission.posterUrl && data.submission.posterType?.startsWith('image/') && (
            <a href={data.submission.posterUrl} target="_blank" rel="noopener">
              <img
                src={data.submission.posterUrl}
                alt="Locandina della segnalazione"
                className="h-28 w-auto border border-surface-strong object-contain"
              />
            </a>
          )}
          <div className="flex flex-col gap-1">
            <span>
              Segnalazione di{' '}
              <strong className="font-medium">
                {data.submission.name ? `${data.submission.name} · ` : ''}
                {data.submission.email ??
                  (data.submission.fromTelegram
                    ? 'inserito dal bot Telegram'
                    : 'profilo cancellato')}
              </strong>
              {whoLabel(data.submission) && (
                <span className="ml-1 bg-accent-soft px-1.5 py-0.5 text-xs">
                  {whoLabel(data.submission)}
                </span>
              )}
              {data.submission.phone && (
                <>
                  {' · '}
                  <a
                    href={`tel:${data.submission.phone.replace(/[^+0-9]/g, '')}`}
                    className="underline"
                  >
                    tel. {data.submission.phone}
                  </a>
                </>
              )}{' '}
              <span className="text-muted">· {formatStamp(data.submission.createdAt)}</span>
            </span>
            {data.submission.userSub && (
              <TrustedToggle
                sub={data.submission.userSub}
                initial={data.submission.trusted === true}
              />
            )}
            {data.submission.posterUrl && (
              <a
                href={data.submission.posterUrl}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-1 font-medium underline-offset-4 hover:underline"
              >
                Locandina originale <ExternalLink className="size-3.5" aria-hidden />
              </a>
            )}
            {data.submission.link && (
              <a
                href={data.submission.link}
                target="_blank"
                rel="noopener"
                className="break-all text-muted underline-offset-4 hover:underline"
              >
                {data.submission.link}
              </a>
            )}
          </div>
        </div>
      )}

      {/* schede degli eventi simili */}
      {matches.length > 0 ? (
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Eventi simili">
          {matches.map((m, i) => (
            <button
              key={m.id}
              role="tab"
              aria-selected={tab === i}
              onClick={() => {
                setTab(i);
                setPicks({});
              }}
              className={`h-8 px-2 text-xs ${tab === i ? 'bg-fg text-page' : 'bg-surface hover:bg-surface-strong'}`}
              title={`${m.title} (${i + 1})`}
            >
              {i + 1}. {m.kind === 'candidate' ? 'candidato' : 'evento'} · {score(m.score)}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted">Nessun evento simile nello stesso comune e periodo.</p>
      )}

      {/* azioni */}
      {open ? (
        <div className="flex flex-col gap-3 border border-surface-strong bg-surface p-3">
          {event ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-label font-medium uppercase tracking-[0.04em] text-muted">
                  È lo stesso evento
                </span>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    disabled={busy || !preview || rewriting}
                    onClick={merge}
                    className={btnPrimary}
                    title="Applica le scelte della tabella all’evento esistente; la versione della sorgente esce dalla coda (u)"
                  >
                    <Merge className="size-4" aria-hidden /> Aggiorna l’evento esistente
                  </button>
                  <span className="text-sm">
                    {previewError ? (
                      <span className="text-danger">Anteprima non disponibile: {previewError}</span>
                    ) : !preview ? (
                      <span className="text-muted">Calcolo le modifiche…</span>
                    ) : changes.length ? (
                      <>
                        Cambieranno: <strong>{changes.join(', ')}</strong>
                      </>
                    ) : (
                      <span className="text-muted">
                        Nessuna modifica: l’evento resta com’è, si registra solo che la sorgente lo
                        riporta
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 border-t border-surface-strong pt-3">
                <span className="mr-1 text-label font-medium uppercase tracking-[0.04em] text-muted">
                  Non è lo stesso
                </span>
                <CreateButtons />
              </div>
            </>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <CreateButtons />
            </div>
          )}
        </div>
      ) : (
        row.review?.eventId && (
          <Link
            to={`/events/${row.review.eventId}`}
            className="text-sm font-medium hover:underline"
          >
            Apri l’evento
          </Link>
        )
      )}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      {event && open && (
        <p className="text-sm text-muted">
          Scegli quale versione tenere dall’intestazione della colonna. Clicca una cella per
          prendere quel campo dall’altra versione. In verde quello che resterà nell’evento.
        </p>
      )}

      {/* confronto campo per campo */}
      <table className="w-full table-fixed border-collapse text-sm">
        <thead>
          <tr className="border-b-2 border-fg text-left align-bottom">
            <th className="w-24 py-2 pr-2" />
            <th className="py-2 pr-3 pl-2 font-normal">
              <ColumnHead
                title="Versione della sorgente"
                selectable={Boolean(event && open)}
                selected={keep === 'candidate'}
                onSelect={() => {
                  setRewriteFailed(false);
                  setKeep('candidate');
                  setPicks({});
                }}
              />
            </th>
            {match && (
              <th className="py-2 pl-2 font-normal">
                <ColumnHead
                  title={
                    match.kind === 'candidate' ? (
                      <Link to={`/review/${match.id}`} className="hover:underline">
                        Candidato in revisione
                      </Link>
                    ) : (
                      <Link to={`/events/${match.id}`} className="hover:underline">
                        Evento esistente
                      </Link>
                    )
                  }
                  extra={
                    <>
                      {event && <StatusBadge status={event.status} />}
                      {event && event.status !== 'draft' && (
                        <a
                          href={`${config.siteUrl}/events/${event.slug}`}
                          target="_blank"
                          rel="noopener"
                          title="Apri sul sito"
                        >
                          <ExternalLink className="size-3.5" aria-hidden />
                        </a>
                      )}
                    </>
                  }
                  selectable={Boolean(event && open)}
                  selected={keep === 'event'}
                  onSelect={() => {
                    setKeep('event');
                    setPicks({});
                  }}
                />
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {FIELDS.map((f) => {
            const o = preview?.origin[f];
            const avail = preview?.available[f];
            const mergeable = Boolean(event && open && preview);
            // completato: resta il valore dell'evento, con le informazioni aggiunte
            const taken = (side: Side) => o === side || (o === 'filled' && side === 'event');
            return (
              <tr key={f} className="border-b border-surface-strong align-top">
                <th className="py-2 pr-2 text-left text-xs font-normal text-muted">
                  {FIELD_LABEL[f]}
                  {picks[f] && picks[f] !== keep && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-fg">
                      <ArrowLeftRight className="size-3" aria-hidden /> spostato
                    </div>
                  )}
                </th>
                {(['candidate', 'event'] as const).map((side) =>
                  side === 'event' && !match ? null : (
                    <Cell
                      key={side}
                      strong={f === 'title'}
                      selected={
                        mergeable &&
                        Boolean(o) &&
                        (f === 'texts' && wantsSourceTexts && !enriched
                          ? side === 'candidate'
                          : taken(side))
                      }
                      filledFrom={o === 'filled' && side === 'event' ? 'della sorgente' : undefined}
                      clickable={
                        mergeable &&
                        o !== side &&
                        (Boolean(avail?.[side]) ||
                          (f === 'texts' && side === 'candidate' && hasSourceText))
                      }
                      onPick={() => pickField(f, side)}
                    >
                      {side === 'event' && !event ? (
                        <span className="text-muted">
                          {f === 'title'
                            ? match?.title
                            : f === 'dates' && match
                              ? formatRange(match.startDate, match.endDate)
                              : ''}
                        </span>
                      ) : (
                        cell[f][side]
                      )}
                    </Cell>
                  ),
                )}
              </tr>
            );
          })}
          {match && (
            <tr className="align-top">
              <th className="py-2 pr-2 text-left text-xs font-normal text-muted">Somiglianza</th>
              <td className="py-2 pr-3 text-xs text-muted" colSpan={2}>
                {score(match.score)} · titolo {score(match.titleScore)} · date{' '}
                {score(match.dateScore)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {event && open && (
        <p className="text-xs text-muted">
          In verde quello che resta nell’evento. Clicca una cella per prendere quel campo dall’altra
          versione: sovrascrive, anche se poi quella versione esce dalla coda. I campi vuoti si
          completano da soli con l’altra versione.
        </p>
      )}
      {c.issues.length > 0 && (
        <ul className="text-xs">
          {c.issues.map((i) => (
            <li key={i.code} className={i.severity === 'error' ? 'text-danger' : 'text-muted'}>
              {i.severity === 'error' ? '✕' : i.severity === 'warning' ? '⚠' : 'ℹ'} {i.message}
            </li>
          ))}
        </ul>
      )}
      {en && (
        <p className="text-xs text-muted">
          Testi riscritti: {en.model} · {en.promptVersion}
        </p>
      )}
      <p className="text-xs text-muted">{REVIEW_KEYS} · 1-3: evento simile</p>
    </div>
  );
}

export function Cell({
  children,
  strong,
  selected,
  filledFrom,
  clickable,
  onPick,
}: {
  children: ReactNode;
  strong?: boolean;
  selected: boolean;
  filledFrom?: string | undefined;
  clickable: boolean;
  onPick: () => void;
}) {
  return (
    <td
      onClick={clickable ? onPick : undefined}
      title={clickable ? 'Usa questo valore nell’evento' : undefined}
      className={[
        'py-2 pr-3 pl-2 align-top',
        strong ? 'font-medium' : '',
        selected ? 'border-l-4 border-l-ok bg-ok-softer' : 'border-l-4 border-l-transparent',
        clickable ? 'cursor-pointer hover:bg-surface' : '',
      ].join(' ')}
    >
      {children}
      {filledFrom && (
        <div className="mt-1 text-[11px] font-normal text-ok">
          + completato con le informazioni {filledFrom}
        </div>
      )}
    </td>
  );
}

export function ColumnHead({
  title,
  extra,
  selectable,
  selected,
  onSelect,
}: {
  title: ReactNode;
  extra?: ReactNode;
  selectable: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <div className="flex flex-col items-start gap-1.5">
      <span className="inline-flex items-center gap-2 text-label font-medium uppercase tracking-[0.04em] text-muted">
        {title}
        {extra}
      </span>
      {selectable && (
        <button
          role="radio"
          aria-checked={selected}
          onClick={onSelect}
          title="Versione da tenere (t per cambiare)"
          className={`inline-flex h-8 items-center gap-2 border px-2 text-sm ${
            selected
              ? 'border-ok bg-ok-softer font-medium text-fg'
              : 'border-fg/25 bg-page text-muted hover:border-fg hover:text-fg'
          }`}
        >
          <span
            aria-hidden
            className={`inline-block size-3.5 rounded-full border-2 ${
              selected
                ? 'border-ok bg-ok shadow-[inset_0_0_0_2px_var(--color-ok-softer)]'
                : 'border-fg/40'
            }`}
          />
          {selected ? 'Versione tenuta' : 'Tieni questa versione'}
        </button>
      )}
    </div>
  );
}

/**
 * Segnalatore fidato: le sue prossime segnalazioni si pubblicano da sole quando la lettura AI è
 * pulita (nessun avviso, affidabilità sufficiente, testi riscritti), anche con la sorgente in
 * revisione.
 */
function TrustedToggle({ sub, initial }: { sub: string; initial: boolean }) {
  const { api } = useSession();
  const [trusted, setTrusted] = useState(initial);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api<{ trusted: boolean }>(
        `/imports/submitters/${encodeURIComponent(sub)}/trusted`,
        {
          method: 'PUT',
          body: { trusted: !trusted },
        },
      );
      setTrusted(r.trusted);
    } finally {
      setBusy(false);
    }
  };
  return (
    <label
      className="flex items-center gap-2 text-sm"
      title="Le sue prossime segnalazioni si pubblicano da sole se la lettura AI è pulita"
    >
      <input type="checkbox" checked={trusted} disabled={busy} onChange={() => void toggle()} />
      Segnalatore fidato
      {trusted && <span className="text-xs text-muted">(pubblicazione automatica)</span>}
    </label>
  );
}
